import type { Point } from '@racemap/utilities/types/types';
import { Space, Spin } from 'antd';
import { type KeyboardEvent, type MouseEvent, useRef, useState } from 'react';
import { TrackPointsImport } from 'src/models/TrackPointsImport';
import { IconFail, IconTrash, IconUpload } from './BasicComponents/Icons';
import { DropZone } from './DropZone';

enum STATUS {
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

type IndicatorProps = {
  status: STATUS | null;
};

function StatusIndicator({ status }: IndicatorProps) {
  switch (status) {
    case STATUS.PROCESSING:
      return (
        <div>
          <Spin /> Processing
        </div>
      );
    case STATUS.FAILURE:
      return (
        <div>
          <IconFail /> Failed
        </div>
      );
    default:
      return <div />;
  }
}

type Track = {
  points: Array<Point>;
  name: string;
};

type Props = {
  onChange: (arg0: Track | null) => void;
  value: Track | null;
  id: string;
};

export function TrackDropView(props: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<STATUS | null>(null);

  async function handleFilesDrop(files: FileList | null) {
    if (files == null || files.length === 0) return;

    const file = files[0];
    setStatus(STATUS.PROCESSING);
    try {
      const importer = new TrackPointsImport();
      const points = await importer.importFile(file);
      props.onChange({ points, name: file.name });
      setStatus(STATUS.SUCCESS);
    } catch {
      setStatus(STATUS.FAILURE);
    }
  }

  function handleDropTargetClick() {
    const inputField = fileInputRef.current;
    if (inputField != null) {
      inputField.click();
    }
  }

  function handleRemoveClick(
    event: MouseEvent<SVGSVGElement, globalThis.MouseEvent> | KeyboardEvent<SVGSVGElement>,
  ) {
    event.stopPropagation();
    props.onChange(null);
  }

  return (
    <DropZone
      className="track-dropview"
      onFilesDrop={handleFilesDrop}
      disabled={false}
      showOverlay={false}
      allowedItemKind="file"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: a mouse shortcut to the file input; it wraps the remove button, so it cannot be a button */}
      <div
        className="drop-target"
        onClick={handleDropTargetClick}
        onKeyUp={(e) => e.key === 'Enter' && handleDropTargetClick()}
      >
        {props.value != null ? (
          <div>
            <Space>
              <div className="track-points-info">
                {props.value.name} ({props.value.points.length} points)
              </div>
              <IconTrash
                className="pointer remove-track"
                onClick={(e) => handleRemoveClick(e)}
                onKeyUp={(e) => e.key === 'Enter' && handleRemoveClick(e)}
              />
            </Space>
          </div>
        ) : (
          <div>
            <IconUpload className="upload-button" />
            <input
              type="file"
              className="upload-image"
              multiple={false}
              style={{ display: 'none' }}
              id={props.id}
              ref={fileInputRef}
              onChange={(e) => handleFilesDrop(e.target.files)}
            />
          </div>
        )}
      </div>
      <StatusIndicator status={status} />
    </DropZone>
  );
}
